import React, { useState, useEffect } from "react";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_BASE_URL || "http://localhost:3051";

interface Stats {
  totalChanges: number;
  bySchedule: Record<string, number>;
  byDevice: Record<string, number>;
  byGateway: Record<string, number>;
  byHour: number[];
  averagePositionChange: number;
  topDevices: Array<{ device: string; count: number }>;
  topGateways: Array<{ gateway: string; count: number }>;
  gatewaySwitchingPatterns?: Array<{
    from: string;
    to: string;
    count: number;
    avgPositionChange: number;
  }>;
  positionChangeDistribution?: {
    minor: number; // 0-5%
    moderate: number; // 5-10%
    significant: number; // 10-20%
    severe: number; // 20%+
  };
  lowDominanceDevices?: Array<{
    device: string;
    avgDominance: number;
    changeCount: number;
  }>;
  dateRange: {
    start: string;
    end: string;
  };
}

interface StatsPanelProps {
  password: string;
}

const StatsPanel: React.FC<StatsPanelProps> = ({ password }) => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true); // Start with true for initial load
  const [refreshing, setRefreshing] = useState(false);
  const [monitoringStatus, setMonitoringStatus] = useState<any>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(10); // seconds
  const [showIntervalModal, setShowIntervalModal] = useState(false);
  const [tempInterval, setTempInterval] = useState(10);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0],
    endDate: new Date().toISOString().split("T")[0],
    startTime: "",
    endTime: "",
  });

  useEffect(() => {
    fetchAll(true); // Initial load
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (autoRefresh) {
      interval = setInterval(fetchAll, refreshInterval * 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, refreshInterval, dateRange]);

  const handleIntervalSave = () => {
    setRefreshInterval(tempInterval);
    setShowIntervalModal(false);
  };

  const handleIntervalCancel = () => {
    setTempInterval(refreshInterval);
    setShowIntervalModal(false);
  };

  const fetchAll = async (isInitialLoad = false) => {
    if (isInitialLoad) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }

    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      if (dateRange.startDate) params.append("startDate", dateRange.startDate);
      if (dateRange.endDate) params.append("endDate", dateRange.endDate);
      if (dateRange.startTime) params.append("startTime", dateRange.startTime);
      if (dateRange.endTime) params.append("endTime", dateRange.endTime);

      // Fetch both stats and status in parallel
      const [statsResponse, statusResponse] = await Promise.all([
        axios.get(
          `${API_BASE_URL}/api/dominant-monitoring/stats?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "x-admin-password": password,
            },
          }
        ),
        axios.get(`${API_BASE_URL}/api/dominant-monitoring/status`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "x-admin-password": password,
          },
        }),
      ]);

      if (statsResponse.data.success) {
        setStats(statsResponse.data.stats);
      }

      if (statusResponse.data.success) {
        setMonitoringStatus(statusResponse.data.status);
      }
    } catch (err: any) {
      console.error("Failed to fetch data:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="text-center py-8 text-gray-500">
          Loading statistics...
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="text-center py-8 text-gray-500">
          No statistics available
        </div>
      </div>
    );
  }

  const maxHourValue = Math.max(...stats.byHour);

  return (
    <div className="space-y-6">
      {/* Monitoring Status Indicator */}
      {monitoringStatus && (
        <div
          className={`rounded-lg shadow-sm p-4 ${
            monitoringStatus.inAbandonedPeriod
              ? "bg-green-50 border-2 border-green-500"
              : "bg-gray-50 border-2 border-gray-300"
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className={`w-3 h-3 rounded-full ${
                  monitoringStatus.inAbandonedPeriod
                    ? "bg-green-500 animate-pulse"
                    : "bg-gray-400"
                }`}
              />
              <div>
                <p className="font-bold text-gray-900">
                  {monitoringStatus.inAbandonedPeriod
                    ? "🟢 Currently Monitoring"
                    : "⏸️ Monitoring Inactive"}
                </p>
                <p className="text-sm text-gray-600">
                  {monitoringStatus.inAbandonedPeriod
                    ? `Active schedule: ${
                        monitoringStatus.activeSchedule || "Unknown"
                      }`
                    : monitoringStatus.enabled
                    ? `Waiting for next schedule (${monitoringStatus.totalSchedules} configured)`
                    : "Monitoring is disabled"}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Current time: {monitoringStatus.currentTime}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Date Range Selector */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">
            📊 Statistics Dashboard
          </h2>
          <div className="flex items-center gap-3">
            {/* Auto-refresh toggle */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setTempInterval(refreshInterval);
                  setShowIntervalModal(true);
                }}
                className="text-sm text-gray-600 hover:text-gray-900 cursor-pointer underline decoration-dotted"
              >
                Auto-refresh ({refreshInterval}s)
              </button>
              <button
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  autoRefresh ? "bg-green-600" : "bg-gray-300"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    autoRefresh ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
            {/* Manual refresh button */}
            <button
              onClick={() => fetchAll(false)}
              disabled={refreshing}
              className="p-2 text-gray-600 hover:text-gray-800 transition-colors disabled:opacity-50"
              title="Refresh all data"
            >
              <svg
                className={`w-5 h-5 ${refreshing ? "animate-spin" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) =>
                setDateRange({ ...dateRange, startDate: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Time
            </label>
            <input
              type="time"
              value={dateRange.startTime}
              onChange={(e) =>
                setDateRange({ ...dateRange, startTime: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) =>
                setDateRange({ ...dateRange, endDate: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Time
            </label>
            <input
              type="time"
              value={dateRange.endTime}
              onChange={(e) =>
                setDateRange({ ...dateRange, endTime: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
          <div>
            <button
              onClick={() => fetchAll(false)}
              className="w-full px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
            >
              Update Stats
            </button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Anomalies</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">
                {stats.totalChanges}
              </p>
            </div>
            <div className="text-4xl">🚨</div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Avg Position Change</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">
                {stats.averagePositionChange}%
              </p>
            </div>
            <div className="text-4xl">📏</div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Date Range</p>
              <p className="text-sm font-medium text-gray-900 mt-1">
                {stats.dateRange.start}
              </p>
              <p className="text-sm font-medium text-gray-900">
                to {stats.dateRange.end}
              </p>
            </div>
            <div className="text-4xl">📅</div>
          </div>
        </div>
      </div>

      {/* Top Devices and Gateways */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Top Devices */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">
            📱 Most Problematic Devices
          </h3>
          {stats.topDevices.length === 0 ? (
            <p className="text-gray-500 text-sm">No data available</p>
          ) : (
            <div className="space-y-2">
              {stats.topDevices.map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div>
                    <p className="font-medium text-gray-900">{item.device}</p>
                    <p className="text-xs text-gray-500">
                      {idx + 1}. Most anomalies
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-red-600">
                      {item.count}
                    </p>
                    <p className="text-xs text-gray-500">changes</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top Gateways */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">
            📡 Most Involved Gateways
          </h3>
          {stats.topGateways.length === 0 ? (
            <p className="text-gray-500 text-sm">No data available</p>
          ) : (
            <div className="space-y-2">
              {stats.topGateways.map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div>
                    <p className="font-medium text-gray-900">{item.gateway}</p>
                    <p className="text-xs text-gray-500">
                      {idx + 1}. Most involved
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-orange-600">
                      {item.count}
                    </p>
                    <p className="text-xs text-gray-500">changes</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Hourly Distribution */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">
          🕐 Hourly Distribution
        </h3>
        <div
          className="flex items-end justify-between gap-1"
          style={{ height: "200px" }}
        >
          {stats.byHour.map((count, hour) => {
            const maxHeight = 200; // pixels
            const heightPx =
              maxHourValue > 0
                ? Math.max(
                    (count / maxHourValue) * maxHeight,
                    count > 0 ? 8 : 0
                  )
                : 0;

            return (
              <div
                key={hour}
                className="flex-1 flex flex-col items-center justify-end"
              >
                <div
                  className="w-full bg-blue-500 rounded-t transition-all hover:bg-blue-600 cursor-pointer"
                  style={{
                    height: `${heightPx}px`,
                  }}
                  title={`${hour}:00 - ${count} anomalies`}
                />
                <p className="text-xs text-gray-500 mt-1">{hour}</p>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-gray-500 text-center mt-2">
          Hour of day (0-23)
        </p>
      </div>

      {/* Gateway Switching Patterns and Low Dominance Devices - Side by Side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Gateway Switching Patterns */}
        {stats.gatewaySwitchingPatterns &&
          stats.gatewaySwitchingPatterns.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                🔄 Gateway Switching Patterns
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Most frequent gateway transitions - helps identify unstable
                gateway pairs
              </p>
              <div className="space-y-2">
                {stats.gatewaySwitchingPatterns
                  .slice(0, 10)
                  .map((pattern, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-red-600">
                          {pattern.from}
                        </span>
                        <span className="text-gray-400">→</span>
                        <span className="text-sm font-medium text-green-600">
                          {pattern.to}
                        </span>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-xs text-gray-500">
                            Avg Position Δ
                          </p>
                          <p className="text-sm font-medium text-gray-900">
                            {pattern.avgPositionChange.toFixed(1)}%
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500">Switches</p>
                          <p className="text-lg font-bold text-gray-900">
                            {pattern.count}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

        {/* Low Dominance Devices */}
        {stats.lowDominanceDevices && stats.lowDominanceDevices.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              ⚠️ Low Dominance Devices
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Devices with low average dominance (&lt;20%) - signals spread
              across many gateways
            </p>
            <div className="space-y-2">
              {stats.lowDominanceDevices.map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg border border-yellow-200"
                >
                  <div>
                    <p className="font-medium text-gray-900">{item.device}</p>
                    <p className="text-xs text-gray-500">
                      {item.changeCount} changes
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Avg Dominance</p>
                    <p className="text-lg font-bold text-yellow-700">
                      {item.avgDominance}%
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Position Change Distribution */}
      {stats.positionChangeDistribution && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">
            📏 Position Change Distribution
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm text-gray-600 mb-1">Minor (0-5%)</p>
              <p className="text-2xl font-bold text-green-700">
                {stats.positionChangeDistribution.minor}
              </p>
            </div>
            <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <p className="text-sm text-gray-600 mb-1">Moderate (5-10%)</p>
              <p className="text-2xl font-bold text-yellow-700">
                {stats.positionChangeDistribution.moderate}
              </p>
            </div>
            <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
              <p className="text-sm text-gray-600 mb-1">Significant (10-20%)</p>
              <p className="text-2xl font-bold text-orange-700">
                {stats.positionChangeDistribution.significant}
              </p>
            </div>
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <p className="text-sm text-gray-600 mb-1">Severe (20%+)</p>
              <p className="text-2xl font-bold text-red-700">
                {stats.positionChangeDistribution.severe}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* By Schedule */}
      {Object.keys(stats.bySchedule).length > 0 && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">
            📅 Anomalies by Schedule
          </h3>
          <div className="space-y-2">
            {Object.entries(stats.bySchedule)
              .sort((a, b) => b[1] - a[1])
              .map(([schedule, count]) => (
                <div
                  key={schedule}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <p className="font-medium text-gray-900">{schedule}</p>
                  <div className="flex items-center gap-3">
                    <div className="w-48 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{
                          width: `${(count / stats.totalChanges) * 100}%`,
                        }}
                      />
                    </div>
                    <p className="text-lg font-bold text-gray-900 w-12 text-right">
                      {count}
                    </p>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Interval Settings Modal */}
      {showIntervalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              Auto-Refresh Interval
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Set how often the statistics should automatically refresh (in
              seconds)
            </p>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Refresh Interval: {tempInterval} seconds
              </label>
              <input
                type="range"
                min="1"
                max="60"
                value={tempInterval}
                onChange={(e) => setTempInterval(parseInt(e.target.value))}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>1s</span>
                <span>30s</span>
                <span>60s</span>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={handleIntervalCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleIntervalSave}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StatsPanel;
